import { FC, useEffect, useRef, useState } from 'react';
import styles from './deep-dive-hint.module.scss';
import { classnames } from '@/utils/functions';
import XmarkIcon from '@/assets/icons/xmark.svg?react';

const AUTO_CLOSE_SECONDS = 8;
const OPEN_DIALOG = '[role="dialog"][aria-modal="true"]:not([inert])';
const MESSAGE =
  'Deep Dive is on because you used it in your last request here.';

interface DeepDiveHintProps {
  isVisible: boolean;
  // Closes it this time; it can show again on a later visit
  onHide: () => void;
  // Closes it for good
  onDismiss: () => void;
  className?: string;
}

/*
 * The bubble mounts each time the hint shows, so its countdown starts fresh.
 * The countdown pauses while a mouse or keyboard focus is on it, so nobody
 * loses the hint mid-read. Touch is left out: a tap sends no matching leave,
 * which would pause it for good.
 */
const HintBubble: FC<Omit<DeepDiveHintProps, 'isVisible' | 'className'>> = ({
  onHide,
  onDismiss,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isPaused = isHovered || isFocused;

  // onHide changes when the switch flips; a ref keeps that from resetting
  // the second in progress
  const onHideRef = useRef(onHide);
  useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [isPaused]);

  useEffect(() => {
    if (secondsLeft <= 0) onHideRef.current();
  }, [secondsLeft]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // That Escape belongs to the open dialog, which closes on it too. A
      // closed modal can stay mounted, made inert
      if (document.querySelector(OPEN_DIALOG)) return;
      onHideRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className={styles['deep-dive-hint-bubble']}
      data-testid="deep-dive-hint"
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setIsHovered(true);
      }}
      onPointerLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsFocused(false);
        }
      }}
    >
      {/* Announced through the live region instead */}
      <p aria-hidden="true">{MESSAGE}</p>
      <button
        type="button"
        aria-label="Close hint"
        className={styles['deep-dive-hint-close']}
        onClick={onHide}
      >
        <XmarkIcon aria-hidden="true" />
      </button>
      <div className={styles['deep-dive-hint-footer']}>
        <button
          type="button"
          className={styles['deep-dive-hint-dismiss']}
          onClick={onDismiss}
        >
          Don't show this again
        </button>
        {/* Hidden from screen readers so they aren't read every second */}
        <span
          className={styles['deep-dive-hint-countdown']}
          data-testid="deep-dive-hint-countdown"
          data-paused={isPaused}
          aria-hidden="true"
        >
          Closes in {secondsLeft}s
        </span>
      </div>
    </div>
  );
};

/*
 * Explains why Deep Dive turned on by itself. The live region stays mounted
 * and holds only the message, so screen readers announce it once without the
 * button labels; focus stays where it was.
 */
const DeepDiveHint: FC<DeepDiveHintProps> = ({
  isVisible,
  onHide,
  onDismiss,
  className = '',
}) => (
  <div className={classnames(styles['deep-dive-hint'], className)}>
    <span className={styles['deep-dive-hint-announcement']} aria-live="polite">
      {isVisible ? MESSAGE : ''}
    </span>
    {isVisible && <HintBubble onHide={onHide} onDismiss={onDismiss} />}
  </div>
);

export default DeepDiveHint;
